// Extend by appending; never renumber a code that has been placed on an order.
export interface LabPackage {
  /* * the lab's own package code/name — what goes on the order */
  code: string;
  /* * short human label */
  label: string;
  /* * what it includes, for the picker's hint line */
  analytes: string[];
}
export interface SoilLab {
  key: string;
  name: string;
  shortName: string;
  packages: LabPackage[];
}

export const SOIL_LABS: SoilLab[] = [
  {
    key: "midwest",
    name: "Midwest Laboratories (Omaha, NE)",
    shortName: "Midwest Labs",
    packages: [
      {
        code: "S1A (BASIC)",
        label: "Basic NPK/cation package (Bray P1+P2)",
        analytes: [
          "Organic Matter",
          "Available Phosphorus P1 (Weak Bray)",
          "Available Phosphorus P2 (Strong Bray)",
          "Exchangeable Potassium",
          "Magnesium",
          "Calcium",
          "Hydrogen",
          "Soil pH",
          "Buffer Index (buffer pH)",
          "Cation Exchange Capacity",
          "Percent Base Saturation of cation elements",
        ],
      },
      {
        code: "S1AN",
        label: "Basic + nitrate-N",
        analytes: [
          "Nitrate-N",
          "Organic Matter",
          "P1 Weak Bray P",
          "P2 Strong Bray P",
          "Exchangeable K",
          "Mg",
          "Ca",
          "H",
          "Soil pH",
          "Buffer Index",
          "CEC",
          "Percent Base Saturation",
        ],
      },
      {
        code: "S1B",
        label: "Basic with Olsen P replacing P2 Strong Bray (high-pH soils)",
        analytes: [
          "Organic Matter",
          "P1 Weak Bray P",
          "Olsen Bicarbonate P",
          "Exchangeable K",
          "Mg",
          "Ca",
          "H",
          "Soil pH",
          "Buffer Index",
          "CEC",
          "Percent Base Saturation",
        ],
      },
      {
        code: "S1C",
        label: "Basic + Olsen P (in addition to Bray P1/P2)",
        analytes: [
          "Olsen Bicarbonate P",
          "Organic Matter",
          "P1 Weak Bray P",
          "P2 Strong Bray P",
          "Exchangeable K",
          "Mg",
          "Ca",
          "H",
          "Soil pH",
          "Buffer Index",
          "CEC",
          "Percent Base Saturation",
        ],
      },
      {
        code: "S1AEC",
        label: "Basic + soluble salts",
        analytes: [
          "Soluble Salts (EC)",
          "Organic Matter",
          "P1 Weak Bray P",
          "P2 Strong Bray P",
          "Exchangeable K",
          "Mg",
          "Ca",
          "H",
          "Soil pH",
          "Buffer Index",
          "CEC",
          "Percent Base Saturation",
        ],
      },
      {
        code: "S1ANEC",
        label: "Basic + soluble salts + nitrate-N",
        analytes: [
          "Soluble Salts (EC)",
          "Nitrate-N",
          "Organic Matter",
          "P1 Weak Bray P",
          "P2 Strong Bray P",
          "Exchangeable K",
          "Mg",
          "Ca",
          "H",
          "Soil pH",
          "Buffer Index",
          "CEC",
          "Percent Base Saturation",
        ],
      },
      {
        code: "S2",
        label: "Basic + soluble salts, sodium, excess lime (high-pH soils)",
        analytes: [
          "Soluble Salts",
          "Sodium",
          "Excess Lime",
          "Organic Matter",
          "P1 Weak Bray P",
          "P2 Strong Bray P",
          "Exchangeable K",
          "Mg",
          "Ca",
          "H",
          "Soil pH",
          "Buffer Index",
          "CEC",
          "Percent Base Saturation",
        ],
      },
      {
        code: "S2N",
        label:
          "Basic + nitrate-N, soluble salts, sodium, excess lime (manured / N crops)",
        analytes: [
          "Nitrate-N",
          "Soluble Salts",
          "Sodium",
          "Excess Lime",
          "Organic Matter",
          "P1 Weak Bray P",
          "P2 Strong Bray P",
          "Exchangeable K",
          "Mg",
          "Ca",
          "H",
          "Soil pH",
          "Buffer Index",
          "CEC",
          "Percent Base Saturation",
        ],
      },
      {
        code: "S3C",
        label: "Complete analysis (S1A + S2N + S3 micronutrients)",
        analytes: [
          "Organic Matter",
          "P1 Weak Bray P",
          "P2 Strong Bray P",
          "Exchangeable K",
          "Mg",
          "Ca",
          "H",
          "Soil pH",
          "Buffer Index",
          "CEC",
          "Percent Base Saturation",
          "Nitrate-N",
          "Soluble Salts",
          "Sodium",
          "Excess Lime",
          "Sulfur",
          "Zinc",
          "Manganese",
          "Iron",
          "Copper",
          "Boron",
        ],
      },
      {
        code: "S3M (MEHLICH-3)",
        label: "Mehlich-3 complete (no nitrate/salts)",
        analytes: [
          "Organic Matter",
          "pH",
          "Mehlich-3 Phosphorus",
          "Mehlich-3 Potassium",
          "Mehlich-3 Magnesium",
          "Mehlich-3 Calcium",
          "Mehlich-3 Sodium",
          "Mehlich-3 Sulfur",
          "Mehlich-3 Zinc",
          "Mehlich-3 Manganese",
          "Mehlich-3 Iron",
          "Mehlich-3 Copper",
          "Mehlich-3 Boron",
          "CEC",
          "Percent Base Saturation",
        ],
      },
      {
        code: "S3CM (MEHLICH-3)",
        label: "Mehlich-3 complete + nitrate-N, excess lime, soluble salts",
        analytes: [
          "Organic Matter",
          "pH",
          "Mehlich-3 Phosphorus",
          "Mehlich-3 Potassium",
          "Mehlich-3 Magnesium",
          "Mehlich-3 Calcium",
          "Mehlich-3 Sodium",
          "Mehlich-3 Sulfur",
          "Mehlich-3 Zinc",
          "Mehlich-3 Manganese",
          "Mehlich-3 Iron",
          "Mehlich-3 Copper",
          "Mehlich-3 Boron",
          "CEC",
          "Percent Base Saturation",
          "Nitrate-N",
          "Excess Lime",
          "Soluble Salts",
        ],
      },
      {
        code: "S3",
        label:
          "Micronutrient add-on: full micros (must be combined with an NPK package)",
        analytes: ["Sulfur", "Zinc", "Manganese", "Iron", "Copper", "Boron"],
      },
      {
        code: "S4",
        label:
          "Micronutrient add-on: Zn/Mn/Fe/Cu (must be combined with an NPK package)",
        analytes: ["Zinc", "Manganese", "Iron", "Copper"],
      },
      {
        code: "S5",
        label:
          "Micronutrient add-on: S + Zn (must be combined with an NPK package)",
        analytes: ["Sulfur", "Zinc"],
      },
      {
        code: "S6",
        label:
          "Micronutrient add-on: S/Zn/Mn/B (must be combined with an NPK package)",
        analytes: ["Sulfur", "Zinc", "Manganese", "Boron"],
      },
      {
        code: "SOIL HEALTH BASIC",
        label: "S1AN chemistry + Solvita 1-day CO2-C + Haney test",
        analytes: [
          "S1AN package (Nitrate-N, OM, Bray P1/P2, K, Mg, Ca, H, pH, Buffer Index, CEC, base saturation)",
          "Solvita 1-day CO2-C",
          "Haney test with Soil Health Calculation",
        ],
      },
      {
        code: "SOIL HEALTH COMPLETE",
        label: "S3C chemistry + Solvita 1-day CO2-C + Haney test",
        analytes: [
          "S3C package (OM, Bray P1/P2, K, Mg, Ca, H, pH, Buffer Index, CEC, base saturation, Nitrate-N, Soluble Salts, Sodium, Excess Lime, S, Zn, Mn, Fe, Cu, B)",
          "Solvita 1-day CO2-C",
          "Haney test with Soil Health Calculation",
        ],
      },
    ],
  },
  {
    key: "al_great_lakes",
    name: "A&L Great Lakes Laboratories (Fort Wayne, IN)",
    shortName: "A&L Great Lakes",
    packages: [
      {
        code: "S1",
        label: "Basic soil test",
        analytes: [
          "Organic Matter",
          "Available Phosphorus",
          "Exchangeable Potassium",
          "Magnesium",
          "Calcium",
          "Soil pH",
          "Buffer pH",
          "Cation Exchange Capacity",
          "Percent Base Saturation of Cation Elements",
        ],
      },
      {
        code: "S1A",
        label: "Basic + Strong Bray P (Bray P2)",
        analytes: [
          "Organic Matter",
          "Available Phosphorus",
          "Strong Bray Phosphorus (Bray P2)",
          "Exchangeable Potassium",
          "Magnesium",
          "Calcium",
          "Soil pH",
          "Buffer pH",
          "Cation Exchange Capacity",
          "Percent Base Saturation of Cation Elements",
        ],
      },
      {
        code: "S1B",
        label: "Basic + Sodium Bicarbonate (Olsen) P",
        analytes: [
          "Organic Matter",
          "Available Phosphorus",
          "Sodium Bicarbonate Phosphorus (Olsen)",
          "Exchangeable Potassium",
          "Magnesium",
          "Calcium",
          "Soil pH",
          "Buffer pH",
          "Cation Exchange Capacity",
          "Percent Base Saturation of Cation Elements",
        ],
      },
      {
        code: "S1AB",
        label: "Basic + Strong Bray P + Sodium Bicarbonate P",
        analytes: [
          "Organic Matter",
          "Available Phosphorus",
          "Strong Bray Phosphorus (Bray P2)",
          "Sodium Bicarbonate Phosphorus (Olsen)",
          "Exchangeable Potassium",
          "Magnesium",
          "Calcium",
          "Soil pH",
          "Buffer pH",
          "Cation Exchange Capacity",
          "Percent Base Saturation of Cation Elements",
        ],
      },
      {
        code: "S2",
        label: "Basic + soluble salts + sodium",
        analytes: [
          "Organic Matter",
          "Available Phosphorus",
          "Exchangeable Potassium",
          "Magnesium",
          "Calcium",
          "Soil pH",
          "Buffer pH",
          "Cation Exchange Capacity",
          "Percent Base Saturation of Cation Elements",
          "Soluble Salts",
          "Sodium",
        ],
      },
      {
        code: "S3",
        label: "Full micronutrient add-on (S + 5 micros)",
        analytes: ["Sulfur", "Zinc", "Manganese", "Iron", "Copper", "Boron"],
      },
      {
        code: "S4",
        label: "Micronutrient add-on (4 metals, no S/B)",
        analytes: ["Zinc", "Manganese", "Iron", "Copper"],
      },
      {
        code: "S5",
        label: "Micronutrient add-on (S + Zn)",
        analytes: ["Sulfur", "Zinc"],
      },
      {
        code: "S6",
        label: "Micronutrient add-on (S, Zn, Mn, B)",
        analytes: ["Sulfur", "Zinc", "Manganese", "Boron"],
      },
      {
        code: "S7",
        label: "Micronutrient add-on (Zn, Mn, B)",
        analytes: ["Zinc", "Manganese", "Boron"],
      },
      {
        code: "Soil Nitrate and Ammonium",
        label: "Nitrate + ammonium N",
        analytes: ["Nitrate-N", "Ammonium-N"],
      },
      {
        code: "Soil Nitrate (PSNT)",
        label: "Nitrate-N only (pre-sidedress)",
        analytes: ["Nitrate-N"],
      },
      {
        code: "SWI1",
        label: "Wisconsin basic (WI-approved methods/recs)",
        analytes: [
          "Organic Matter",
          "Soil pH",
          "Buffer pH",
          "Phosphorus",
          "Potassium",
        ],
      },
      {
        code: "SNCRCAT",
        label: "Wisconsin cation add-on (NCR methods)",
        analytes: [
          "Calcium",
          "Magnesium",
          "Cation Exchange Capacity",
          "Percent Base Saturation of Cation Elements",
        ],
      },
      {
        code: "SNCRCM",
        label: "Wisconsin cations + full micronutrients (NCR methods)",
        analytes: [
          "Calcium",
          "Magnesium",
          "Cation Exchange Capacity",
          "Percent Base Saturation of Cation Elements",
          "Sulfur",
          "Zinc",
          "Manganese",
          "Iron",
          "Copper",
          "Boron",
        ],
      },
    ],
  },
  {
    key: "suretech",
    name: "SureTech Laboratories (Indianapolis, IN — WinField United)",
    shortName: "SureTech",
    packages: [
      {
        code: "RO",
        label: "Routine Organic Analysis (basic + Zn/S + OM)",
        analytes: [
          "pH",
          "Lime Index (buffer pH)",
          "Organic Matter",
          "P",
          "K",
          "Ca",
          "Mg",
          "Zn",
          "S",
          "CEC",
          "Cation saturations (base saturation)",
          "Ca/Mg ratio",
          "Mg/K ratio",
        ],
      },
      {
        code: "C",
        label: "Complete Analysis (RO + full micronutrients + Na)",
        analytes: [
          "pH",
          "Lime Index (buffer pH)",
          "Organic Matter",
          "P",
          "K",
          "Ca",
          "Mg",
          "Zn",
          "S",
          "CEC",
          "Cation saturations (base saturation)",
          "Ca/Mg ratio",
          "Mg/K ratio",
          "Mn",
          "B",
          "Cu",
          "Fe",
          "Na",
        ],
      },
    ],
  },
  {
    key: "waypoint",
    name: "Waypoint Analytical",
    shortName: "Waypoint",
    packages: [
      {
        code: "S1M",
        label: "Basic Mehlich 3 fertility",
        analytes: [
          "pH",
          "buffer pH",
          "P (Mehlich 3)",
          "K",
          "Ca",
          "Mg",
          "OM (not included at PA/Leola lab - must be requested)",
          "CEC",
          "% cation (base) saturation",
        ],
      },
      {
        code: "S2M",
        label: "Basic + any 2 micros/secondaries",
        analytes: [
          "pH",
          "buffer pH",
          "P (Mehlich 3)",
          "K",
          "Ca",
          "Mg",
          "OM (not at PA lab unless requested)",
          "CEC",
          "% cation saturation",
          "any 2 of: Na, SO4-S, B, Zn, Mn, Fe, Cu (each extra beyond 2 = add'l fee, ~$2)",
        ],
      },
      {
        code: "S3M",
        label: "Complete Mehlich 3 (basic + all micros)",
        analytes: [
          "pH",
          "buffer pH",
          "P (Mehlich 3)",
          "K",
          "Ca",
          "Mg",
          "OM (not at PA lab unless requested)",
          "CEC",
          "% cation saturation",
          "Na",
          "SO4-S",
          "B",
          "Zn",
          "Mn",
          "Fe",
          "Cu",
        ],
      },
      {
        code: "S4M",
        label: "S3M + nitrate (NE lab)",
        analytes: [
          "pH",
          "buffer pH",
          "P",
          "K",
          "Ca",
          "Mg",
          "Na",
          "CEC",
          "% base saturation",
          "S",
          "B",
          "Zn",
          "Fe",
          "Cu",
          "Mn",
          "NO3-N",
          "OM (per website 'S3M plus NO3-N'; NE S4M submittal form does not list OM)",
        ],
      },
      {
        code: "S4MS",
        label: "S3M + nitrate + soluble salts 1:1 (NE lab)",
        analytes: [
          "pH",
          "buffer pH",
          "P",
          "K",
          "Ca",
          "Mg",
          "Na",
          "CEC",
          "% base saturation",
          "S",
          "B",
          "Zn",
          "Fe",
          "Cu",
          "Mn",
          "NO3-N",
          "soluble salts (1:1)",
          "OM (per website; NE form does not list OM)",
        ],
      },
      {
        code: "Basic (Mehlich 1)",
        label: "Mehlich 1 basic (TN/NC/VA)",
        analytes: [
          "pH",
          "buffer pH",
          "P (Mehlich 1)",
          "K",
          "Ca",
          "Mg",
          "OM",
          "CEC",
          "% cation saturation",
        ],
      },
      {
        code: "Basic + (Mehlich 1)",
        label: "Mehlich 1 basic + Mn, Zn (TN/NC/VA)",
        analytes: [
          "pH",
          "buffer pH",
          "P (Mehlich 1)",
          "K",
          "Ca",
          "Mg",
          "OM",
          "CEC",
          "% cation saturation",
          "Mn",
          "Zn",
        ],
      },
      {
        code: "Complete (Mehlich 1)",
        label: "Mehlich 1 complete (TN/NC/VA)",
        analytes: [
          "pH",
          "buffer pH",
          "P (Mehlich 1)",
          "K",
          "Ca",
          "Mg",
          "OM",
          "CEC",
          "% cation saturation",
          "Mn",
          "Zn",
          "Cu",
          "Na",
          "Fe",
          "B",
          "S (Mehlich 3)",
        ],
      },
      {
        code: "SW1",
        label: "Alkaline/Southwest basic (TN, NE)",
        analytes: [
          "pH",
          "buffer pH",
          "OM",
          "CEC",
          "% cation saturation",
          "Olsen P",
          "ammonium acetate K, Mg, Ca, Na",
          "Mehlich 3 P, B, S, Fe, Mn, Cu, Zn",
        ],
      },
      {
        code: "SW2",
        label: "SW1 + nitrate",
        analytes: ["SW1 analytes", "NO3-N"],
      },
      {
        code: "SW3",
        label: "SW1 + nitrate + soluble salts 1:2",
        analytes: ["SW1 analytes", "NO3-N", "soluble salts (1:2)"],
      },
    ],
  },
  {
    key: "ksi",
    name: "KSI Laboratories (Shelbyville, IL)",
    shortName: "KSI",
    packages: [
      {
        code: "Test #1",
        label: "Standard analysis (basic)",
        analytes: ["pH", "P", "K", "OM"],
      },
      {
        code: "Test #2",
        label: "Standard analysis + CEC/bases",
        analytes: [
          "pH",
          "P",
          "K",
          "OM",
          "Ca",
          "Mg",
          "% Base Saturation",
          "CEC",
        ],
      },
      {
        code: "Test #3",
        label: "Micronutrient package (add-on)",
        analytes: ["S", "B", "Zn", "Fe", "Mn", "Cu"],
      },
    ],
  },
  {
    key: "united_soils",
    name: "United Soils, Inc. (Fairbury, IL)",
    shortName: "United Soils",
    packages: [
      {
        code: "S1",
        label: "Fertility & Macronutrients (basic)",
        analytes: [
          "pH",
          "buffer pH",
          "organic matter (color match)",
          "P (Mehlich-3)",
          "K (Mehlich-3)",
          "Ca",
          "Mg",
          "CEC",
          "% base saturation",
        ],
      },
      {
        code: "S2",
        label: "Fertility, Macros & Micronutrients (Mehlich-3 micros)",
        analytes: [
          "pH",
          "buffer pH",
          "organic matter (color match)",
          "P (Mehlich-3)",
          "K (Mehlich-3)",
          "Ca",
          "Mg",
          "CEC",
          "% base saturation",
          "S",
          "Zn",
          "Fe",
          "Mn",
          "Cu",
          "B",
        ],
      },
      {
        code: "S8",
        label: "Fertility, Macros, Micros + LOI OM",
        analytes: [
          "pH",
          "buffer pH",
          "organic matter (color match)",
          "organic matter (LOI)",
          "P (Mehlich-3)",
          "K (Mehlich-3)",
          "Ca",
          "Mg",
          "CEC",
          "% base saturation",
          "S",
          "Zn",
          "Fe",
          "Mn",
          "Cu",
          "B",
        ],
      },
      {
        code: "S3",
        label: "DTPA micronutrient add-on (full micro suite)",
        analytes: [
          "S (monocalcium phosphate extract)",
          "Zn (DTPA)",
          "Fe (DTPA)",
          "Mn (DTPA)",
          "Cu (DTPA)",
          "B (monocalcium phosphate extract)",
        ],
      },
      {
        code: "S5",
        label: "DTPA micronutrient add-on (Zn, S, B)",
        analytes: ["Zn (DTPA)", "S", "B"],
      },
      {
        code: "H1",
        label: "Haney Soil Health Test (partnered lab)",
        analytes: [
          "Haney soil health suite (H3A extract, Solvita CO2-burst, water-extractable organic C/N, soil health score) — analytes not itemized on USI site",
        ],
      },
    ],
  },
];

export const DEFAULT_LAB_KEY = "midwest";

export function labByKey(key: string | null | undefined): SoilLab | undefined {
  return SOIL_LABS.find((l) => l.key === key);
}
export function packagesFor(labKey: string | null | undefined): LabPackage[] {
  return labByKey(labKey)?.packages ?? [];
}
/* * the lab's first listed package (its basic/standard one), or '' when the lab lists none */
export function defaultPackageFor(labKey: string | null | undefined): string {
  return packagesFor(labKey)[0]?.code ?? "";
}
